import html2canvas from 'html2canvas';
import type { BugReportConnectionInfo } from '@clareefai/types';

export async function captureScreenshot(): Promise<string> {
  const canvas = await html2canvas(document.body, {
    useCORS: true,
    logging: false,
    scale: 1,
  });
  return canvas.toDataURL('image/jpeg', 0.7).replace(/^data:image\/jpeg;base64,/, '');
}

interface NavigatorWithConnection extends Navigator {
  connection?: {
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
  };
}

export function captureConnectionInfo(): BugReportConnectionInfo {
  const nav = navigator as NavigatorWithConnection;
  return {
    userAgent: navigator.userAgent,
    screenWidth: screen.width,
    screenHeight: screen.height,
    windowWidth: window.innerWidth,
    windowHeight: window.innerHeight,
    url: window.location.href,
    language: navigator.language,
    platform: navigator.platform,
    effectiveType: nav.connection?.effectiveType,
    downlink: nav.connection?.downlink,
    rtt: nav.connection?.rtt,
  };
}

// Redact anything that looks like a credential or session by default, rather than
// maintaining an exact-match blocklist that silently misses keys like `clareef_auth`
// or impersonation-session ids. The captured snapshot is POSTed to the backend, so a
// missed key leaks a live token to anyone who can read bug reports.
const SENSITIVE_KEY_PATTERN = /token|auth|session|secret|password|credential|firebase|clareef_/i;

export function captureLocalStorage(): Record<string, string> {
  const result: Record<string, string> = {};
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;
    result[key] = SENSITIVE_KEY_PATTERN.test(key)
      ? '[REDACTED]'
      : localStorage.getItem(key) ?? '';
  }
  return result;
}

export function describeElement(el: Element): string {
  const tag = el.tagName.toLowerCase();
  const text = (el as HTMLElement).innerText?.trim().slice(0, 40);
  const id = el.id ? `#${el.id}` : '';
  const ariaLabel = el.getAttribute('aria-label');

  if (tag === 'button' || el.getAttribute('role') === 'button') {
    return `Button: ${text || ariaLabel || id || '(unnamed)'}`;
  }
  if (tag === 'a') {
    return `Link: ${text || ariaLabel || id || '(unnamed)'}`;
  }
  if (tag === 'input') {
    const type = (el as HTMLInputElement).type;
    return `Input${id}[${type}]`;
  }
  if (tag === 'select') {
    return `Select${id}`;
  }
  if (tag === 'textarea') {
    return `Textarea${id}`;
  }
  return `${tag}${id}${text ? `: ${text}` : ''}`;
}
